<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import PropertySheetLayout from './PropertySheetLayout.vue'
import type { SortByOption } from '../types/propertyTypes'

// Sort sheet (design: Sort — Live / Launching Soon / Fully Funded). The
// options come from the filters endpoint per tab; picking one edits a draft
// that "Apply Sort" commits.

interface Props {
  options: SortByOption[]
  modelValue: string
  isSheet?: boolean
}

const props = withDefaults(defineProps<Props>(), { isSheet: false })
const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'apply' | 'close'): void
}>()

// No explicit sort yet → the first option (the backend default) reads as selected.
const selectedValue = computed(() => props.modelValue || props.options[0]?.value || '')
</script>

<template>
  <PropertySheetLayout title="Sort" apply-label="Apply Sort" :is-sheet="isSheet" @apply="emit('apply')">
    <template #header-action>
      <Box
        is="button"
        type="button"
        align="center"
        justify="center"
        class="sort-panel__close"
        aria-label="Close sort"
        @click="emit('close')"
      >
        <PiIcon name="times" class="sort-panel__close-icon" />
      </Box>
    </template>

    <Box is="ul" direction="col" class="sort-panel__list" role="radiogroup" aria-label="Sort by">
      <Box
        v-for="option in options"
        :key="option.value"
        is="li"
        role="radio"
        tabindex="0"
        align="center"
        justify="between"
        gap="3"
        class="sort-panel__option"
        :aria-checked="selectedValue === option.value"
        @click="emit('update:modelValue', option.value)"
        @keydown.enter.prevent="emit('update:modelValue', option.value)"
        @keydown.space.prevent="emit('update:modelValue', option.value)"
      >
        <Text is="span" type="heading" weight="regular">{{ option.label }}</Text>
        <Box
          class="sort-panel__radio"
          :class="{ 'sort-panel__radio--checked': selectedValue === option.value }"
          aria-hidden="true"
        />
      </Box>
    </Box>
  </PropertySheetLayout>
</template>

<style scoped>
.sort-panel__close {
  position: relative;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 100px;
  background: var(--p-surface-200);
  cursor: pointer;
  flex-shrink: 0;
}

/* 28px visual, 44px touch target. */
.sort-panel__close::before {
  content: '';
  position: absolute;
  inset: -8px;
}

.sort-panel__close-icon {
  font-size: 11px;
  color: var(--p-text-color);
}

.sort-panel__list {
  gap: 22px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.sort-panel__option {
  min-height: 24px;
  cursor: pointer;
  border-radius: 6px;
}

.sort-panel__option:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 4px;
}

/* 24px radio: grey ring unchecked; brand ring + dot checked. */
.sort-panel__radio {
  width: 20px;
  height: 20px;
  margin: 2px;
  border-radius: 50%;
  border: 1.5px solid var(--p-surface-300);
  flex-shrink: 0;
  box-sizing: border-box;
}

.sort-panel__radio--checked {
  border: 2px solid var(--p-primary-600);
  background: radial-gradient(circle, var(--p-primary-600) 0 5px, transparent 5.5px);
}
</style>
